import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ConsumableInvoiceDocument } from './BillInvoicePreview';
import { printHtml } from '../utils/printHtml';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

interface Patient {
  id: number;
  patient_name: string;
  age: string;
  sex: string;
  treating_hospital?: string;
  treating_doctor?: string;
  treating_details?: { doctor: string; hospital: string; department: string }[];
  case_start_date: string;
  address: string;
  patient_clients: { client_name: string; contact_number_1: string; contact_number_2?: string; email: string }[];
}

interface Props {
  billId: number;
  patient: Patient;
  onClose: () => void;
}

const BillViewModal: React.FC<Props> = ({ billId, patient, onClose }) => {
  const [billData, setBillData] = useState<any>(null);
  const [consumableItems, setConsumableItems] = useState<any[] | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    axios
      .get(`${API_BASE_URL}/api/billing/bills/${billId}`, { headers })
      .then(async (r) => {
        const bill = r.data;
        setBillData(bill);

        if (bill.bill_type === 'consumables' || bill.bill_type === 'consumable_return') {
          try {
            const pivotRes = await axios.get(
              `${API_BASE_URL}/api/consumables/invoice-items/${billId}`,
              { headers },
            );
            const pivot: { batch_id: number; quantity: number; mrp: number | null }[] = pivotRes.data;

            const items = bill.line_items.map((li: any, idx: number) => {
              const disc_price    = li.rate ?? 0;
              const qty           = pivot[idx]?.quantity ?? (li.shifts_count ?? 0);
              const gst           = li.gst_percent ?? 0;
              const taxable       = disc_price * qty;
              const cgst          = taxable * (gst / 2) / 100;
              const hsnMatch      = (li.details || '').match(/HSN\/SAC:\s*(.+)/);
              const mrp           = pivot[idx]?.mrp ?? null;
              const discount_rate = (mrp != null && mrp > 0)
                ? ((mrp - disc_price) / mrp) * 100
                : null;
              return {
                particulars:   li.particulars,
                hsn_sac:       hsnMatch ? hsnMatch[1].trim() : '',
                mrp,
                discount_rate,
                disc_price,
                quantity:      qty,
                gst_percent:   gst,
                cgst_amount:   cgst,
                sgst_amount:   cgst,
                subtotal:      li.total_amount,
              };
            });
            setConsumableItems(items);
          } catch {
            setConsumableItems(undefined);
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [billId]);

  const handleDownload = () => {
    if (!previewRef.current || !billData) return;
    setDownloading(true);
    const fmtD = (d: string) => d.split('-').reverse().join('-');
    // Consumables/returns carry no bill_no or billing period — name them off their
    // own invoice series instead, or the file comes out as "…_null_…".
    const slugNo = (n: string) => (n || 'draft').replace(/[/\s]+/g, '-');
    const fileName =
      billData.bill_type === 'consumables'
        ? `Consumable_Invoice_${patient.patient_name}_${slugNo(billData.invoice_no)}`
        : billData.bill_type === 'consumable_return'
          ? `Consumable_Return_${patient.patient_name}_${slugNo(billData.invoice_no)}`
          : ['medical_equipment', 'equipment_rental', 'equipment_sale'].includes(billData.bill_type)
            ? `MedicalEquipment_${patient.patient_name}_${slugNo(billData.invoice_no)}`
            : `Nursing_Billing_${patient.patient_name}_${billData.bill_no}_${fmtD(billData.invoice_period_start)} to ${fmtD(billData.invoice_period_end)}`;
    printHtml(fileName, previewRef.current.outerHTML, () => setDownloading(false));
  };

  // Every field the invoice renders has to be carried across. The GST columns
  // and the equipment dates were being dropped here, so a bill viewed from the
  // bills list printed without them while the same bill previewed from its own
  // edit screen — which passes its full line items — printed correctly.
  const lineItems =
    billData?.line_items?.map((li: any) => {
      // Only has_gst and gst_percent are stored on a line item — there are no
      // cgst_amount / sgst_amount columns — so the split is recomputed here from
      // the rate, exactly as the billing sheet computes it before saving. Taking
      // li.cgst_amount instead would always be undefined, which is what left the
      // CGST/SGST columns showing dashes and the Totals row reading 0.00.
      const hasGst = li.has_gst ?? false;
      const pct = Number(li.gst_percent) || 0;
      const rateNum = Number(li.rate) || 0;
      const halfGst = hasGst && pct > 0
        ? Math.round(rateNum * (pct / 2 / 100) * 100) / 100
        : 0;
      const gstAmount = halfGst * 2;
      return {
        particulars:    li.particulars,
        rate:           li.rate ?? '',
        details:        li.details || '',
        shift_type:     li.shift_type || '',
        shifts_count:   li.shifts_count ?? '',
        total_amount:   li.total_amount,
        is_miscellaneous: li.is_miscellaneous,
        section_type:   li.section_type,
        start_date:     li.item_start_date || '',
        end_date:       li.item_end_date || '',
        has_gst:        hasGst,
        gst_percent:    li.gst_percent ?? 0,
        gst_amount:     gstAmount,
        cgst_amount:    halfGst,
        sgst_amount:    halfGst,
        mrp:            li.mrp ?? '',
        discount_percent: li.discount_percent,
        disc_price:     li.disc_price ?? '',
        quantity:       li.quantity ?? '',
        // Stored on the line item, so the bill prints the code it was raised
        // under even if the device's registration changed since.
        hsn_code:       li.hsn_code || '',
      };
    }) ?? [];

  return (
    <div
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)',
        zIndex: 4000, overflowY: 'auto', display: 'flex',
        flexDirection: 'column', alignItems: 'center', padding: '20px',
      }}
      onClick={onClose}
    >
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }} onClick={(e) => e.stopPropagation()}>
        <button
          style={{ padding: '0.6rem 1.5rem', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', cursor: downloading || loading ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: downloading || loading ? 0.7 : 1 }}
          onClick={handleDownload}
          disabled={downloading || loading}
        >
          {downloading ? 'Opening...' : '🖨 Print / Save PDF'}
        </button>
        <button
          style={{ padding: '0.6rem 1.5rem', backgroundColor: 'white', color: '#334155', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}
          onClick={onClose}
        >
          ✕ Close
        </button>
      </div>

      {loading ? (
        <div style={{ color: 'white', fontSize: '1rem' }}>Loading...</div>
      ) : billData ? (
        <div onClick={(e) => e.stopPropagation()}>
          <ConsumableInvoiceDocument
            ref={previewRef}
            patient={patient}
            billNo={billData.bill_no}
            invoiceNo={billData.invoice_no}
            billDate={billData.bill_date}
            periodStart={billData.invoice_period_start}
            periodEnd={billData.invoice_period_end}
            lineItems={lineItems}
            consumableItems={consumableItems}
            remarks={billData.remarks || ''}
            discountDescription={billData.discount_description || ''}
            discountAmount={billData.discount_amount ?? null}
            createdAt={billData.created_at}
            billType={billData.bill_type}
            hasGst={billData.line_items?.some((li: any) => li.has_gst) ?? false}
          />
        </div>
      ) : (
        <div style={{ color: '#fca5a5' }}>Failed to load bill.</div>
      )}
    </div>
  );
};

export default BillViewModal;
